import * as v from 'valibot';

/**
 * The LLM gateway: one door every model request goes through.
 *
 * A fleet of agents that each hold their own provider key is a fleet with no
 * answer to four questions a platform team is asked in its first month — who
 * spent this, what left the building in a prompt, which agent was told to do
 * something it should not have been, and can we turn one of them off. None of
 * those are answerable at the edges, because the edges are the thing being
 * asked about.
 *
 * So the gateway sits in front of dispatch and does five things, in order, on
 * the way in: it resolves *who* the request is for, strips PII out of what is
 * about to be sent, screens the instruction, applies the deployment's policy,
 * and checks the money. Any one of them can refuse. On the way out it records
 * what was actually spent against a project, a user and a team.
 *
 * The order matters and is not arbitrary. Identity first, because the other
 * four are all "for whom"; redaction before screening, so the screen never sees
 * the PII either; budget last, because refusing an expensive request is only
 * correct once you know it was going to be allowed at all.
 */

/**
 * The five guards, named for what they protect rather than how they work.
 *
 * Five, and no more, because each one has to be explainable to the engineer
 * whose request it refused. A guard nobody can explain is a guard that gets an
 * exception carved for it, and then a second, and then it is not a guard.
 */
export const LlmGuard = v.picklist(['identity', 'pii', 'safety', 'policy', 'budget']);
export type LlmGuard = v.InferOutput<typeof LlmGuard>;

/**
 * What a guard did.
 *
 * `redacted` is not a softer `blocked` — it is the common case. Most prompts
 * that touch PII are perfectly legitimate requests that happened to quote a
 * row, and blocking them teaches everyone to route around the gateway.
 */
export const LlmGuardOutcome = v.picklist(['passed', 'redacted', 'blocked']);
export type LlmGuardOutcome = v.InferOutput<typeof LlmGuardOutcome>;

export const LlmGuardVerdict = v.object({
  guard: LlmGuard,
  outcome: LlmGuardOutcome,
  /** What it found, in the words the refusal shows the caller. Empty when it passed. */
  detail: v.optional(v.string(), ''),
  /** How many values it rewrote. Only meaningful for `pii`. */
  redactions: v.optional(v.pipe(v.number(), v.integer()), 0),
});
export type LlmGuardVerdict = v.InferOutput<typeof LlmGuardVerdict>;

/**
 * Who and what a request is attributed to.
 *
 * Three fields rather than one, because the three questions are asked by three
 * different people: an engineer asks what *they* spent, a team lead asks what
 * the *team* spent, and whoever owns the budget asks what the *project* spent.
 * A single "owner" column answers one of the three and is reconstructed badly
 * for the other two.
 */
export const LlmAttribution = v.object({
  /** The workflow. Reef's unit of ownership and the thing a budget is set on. */
  project: v.string(),
  userId: v.nullable(v.string()),
  /** The group the user belongs to, resolved live at request time. */
  team: v.nullable(v.string()),
  runId: v.optional(v.nullable(v.string()), null),
  stepId: v.optional(v.nullable(v.string()), null),
});
export type LlmAttribution = v.InferOutput<typeof LlmAttribution>;

/**
 * One request as the ledger records it.
 *
 * Written after the response, not before: a row inserted on the way in and
 * updated on the way out is a row that stays half-written whenever an isolate
 * dies mid-call, and a ledger with half-written rows is one people stop
 * trusting for exactly the number it exists to provide.
 */
export const LlmRequestRecord = v.object({
  id: v.string(),
  at: v.string(),
  model: v.string(),
  attribution: LlmAttribution,
  verdicts: v.array(LlmGuardVerdict),
  /** True when any guard blocked. The request never reached a provider. */
  blocked: v.boolean(),
  /** Time the five guards took, in milliseconds. The budget is `GUARD_LATENCY_BUDGET_MS`. */
  guardMs: v.number(),
  promptTokens: v.optional(v.pipe(v.number(), v.integer()), 0),
  completionTokens: v.optional(v.pipe(v.number(), v.integer()), 0),
  costCents: v.optional(v.number(), 0),
});
export type LlmRequestRecord = v.InferOutput<typeof LlmRequestRecord>;

export const LlmGatewayStats = v.object({
  windowHours: v.number(),
  requests: v.pipe(v.number(), v.integer()),
  blocked: v.pipe(v.number(), v.integer()),
  /** Requests where at least one value was rewritten before dispatch. */
  redacted: v.pipe(v.number(), v.integer()),
  spendCents: v.number(),
  promptTokens: v.pipe(v.number(), v.integer()),
  completionTokens: v.pipe(v.number(), v.integer()),
  /** Median and worst-case guard latency, so the budget is a fact and not a claim. */
  guardMsP50: v.nullable(v.number()),
  guardMsP95: v.nullable(v.number()),
  byProject: v.optional(v.array(v.object({ project: v.string(), requests: v.number(), spendCents: v.number() })), []),
  byTeam: v.optional(v.array(v.object({ team: v.string(), requests: v.number(), spendCents: v.number() })), []),
  byModel: v.optional(v.array(v.object({ model: v.string(), requests: v.number(), spendCents: v.number() })), []),
  byGuard: v.optional(v.array(v.object({ guard: v.string(), blocked: v.number(), redacted: v.number() })), []),
  computedAt: v.string(),
});
export type LlmGatewayStats = v.InferOutput<typeof LlmGatewayStats>;

/**
 * What the five guards are allowed to cost.
 *
 * A gateway that adds a second to every request is a gateway teams route
 * around, so the budget is stated as a number and measured on every request
 * rather than asserted in a design document. Everything in the guard chain is
 * therefore local: regexes and set membership, no model call, no network hop.
 */
export const GUARD_LATENCY_BUDGET_MS = 100;
